import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthContext';
import { listConnections, fetchConnectionCategories } from '@/data/connections';
import { pushBlogDraftToWordPress } from '@/data/publications';
import type { BlogDraft } from '@/data/copilot';
import {
  canPush,
  isPushEligible,
  pushSuccessNote,
  selectablePushTargets,
  type PushResult,
  type PushableTarget,
} from './contentActionModel.ts';
import {
  buildCategoryPushOptions,
  categoryGate,
  categoryGateNote,
  categoryNameFor,
  pushCategoryKeyword,
} from './categorySelectModel.ts';

/* ----------------------------------------------------------------------------
 * "Push to WordPress Draft" — the explicit, draft-only publish affordance on an
 * APPROVED (status 'ready') blog draft. This is the deliberate second step after
 * approval: APPROVE != SEND. Approval only makes the draft ELIGIBLE; the user
 * then (1) picks a connected site, (2) confirms/picks the CATEGORY, and (3) clicks
 * the button to push it. The push lands the article in that site's WordPress
 * Posts → Drafts and NOTHING is ever published (the server hard-codes WP status
 * 'draft'; the FE downgrades any non-draft response to a failure).
 *
 * CATEGORY (existing-only): once a site is chosen we read that site's EXISTING
 * categories and the server's best-match suggestion for the draft topic. A
 * confident match is pre-selected and shown (the user may change it); an ambiguous
 * / no-match verdict BLOCKS the push until the user picks an existing category. We
 * never create a category and never silently default to Uncategorized. The chosen
 * id + the site's live category ids ride in push_options.
 * -------------------------------------------------------------------------- */

const primaryBtn =
  'inline-flex items-center justify-center rounded-full border border-ai bg-ai-wash/60 px-3 py-1.5 text-xs font-semibold text-text-primary transition-colors duration-fast hover:bg-ai-wash disabled:cursor-not-allowed disabled:opacity-50 max-lg:min-h-[44px]';
const selectCls =
  'rounded-sm border border-border-default bg-canvas px-2 py-1.5 text-xs text-text-primary focus:border-ai focus:outline-none max-lg:min-h-[44px]';

export function WordPressPushControl({ draft }: { draft: BlogDraft }) {
  const { activeWorkspace } = useAuth();
  const wsId = activeWorkspace?.id ?? 'none';
  const [targetId, setTargetId] = useState<number | null>(null);
  const [categoryChoice, setCategoryChoice] = useState<number | null>(null);
  const [result, setResult] = useState<{ ok: boolean; note: string; editUrl?: string } | null>(null);

  // Eligibility is APPROVAL, not a send: a non-ready / unsaved draft shows nothing.
  const eligible = isPushEligible(draft);

  const connections = useQuery({
    queryKey: ['connector', 'connections', wsId],
    queryFn: ({ signal }) => listConnections(signal),
    enabled: eligible,
  });

  const targets: PushableTarget[] = useMemo(
    () => selectablePushTargets(connections.data ?? []),
    [connections.data],
  );

  const selected = targetId ?? (targets.length === 1 ? targets[0].id : null);

  // Category read (existing-only) for the chosen site + this draft's topic.
  const keyword = pushCategoryKeyword(draft);
  const catQuery = useQuery({
    queryKey: ['connector', 'categories', wsId, selected, keyword],
    queryFn: ({ signal }) => fetchConnectionCategories(selected as number, keyword, signal),
    enabled: eligible && selected != null,
  });
  const categories = catQuery.data?.categories ?? [];
  const suggestion = catQuery.data?.suggestion ?? null;
  const catLoading = selected != null && (catQuery.isLoading || catQuery.isFetching);
  const gate = categoryGate({
    isLoading: catLoading,
    categories,
    suggestion,
    userChoiceId: categoryChoice,
  });
  const categoryReady = selected == null ? false : gate.canProceed;

  const push = useMutation({
    mutationFn: (vars: { tid: number; pushOptions: Record<string, unknown> }): Promise<PushResult> =>
      pushBlogDraftToWordPress(draft.id, vars.tid, vars.pushOptions),
    onSuccess: (res, vars) => {
      const label = targets.find((t) => t.id === vars.tid)?.label ?? '';
      if (res.ok) {
        setResult({ ok: true, note: pushSuccessNote(res, label), editUrl: res.editUrl });
      } else {
        setResult({
          ok: false,
          note: 'The push did not complete as a draft. Nothing was published.',
        });
      }
    },
    onError: () => {
      setResult({
        ok: false,
        note: 'The push could not be completed. Nothing was published — please try again.',
      });
    },
  });

  if (!eligible) return null;

  const ready = canPush(draft, selected) && categoryReady;
  const selectedCategoryName = categoryNameFor(gate.selectedId, categories);

  return (
    <div className="mt-3 rounded-md border border-border-default bg-inset/40 p-3">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
          Push to WordPress
        </span>
        <span className="rounded-sm border border-ai/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-ai">
          Draft only
        </span>
      </div>
      <p className="mb-2 text-[11px] leading-snug text-text-secondary">
        Approved drafts are eligible to push. Pick a connected site, confirm the category, and push
        it — the article is saved as a <span className="font-semibold">WordPress draft</span> for you
        to review and publish yourself. Nothing is published automatically.
      </p>

      {connections.isLoading && (
        <p className="text-[11px] text-text-tertiary">Loading connected sites…</p>
      )}
      {connections.isError && (
        <p className="text-[11px] text-danger">Could not load connected sites.</p>
      )}
      {!connections.isLoading && !connections.isError && targets.length === 0 && (
        <p className="text-[11px] text-text-tertiary">
          No connected WordPress site yet. Connect one under Settings → Connections to push drafts.
        </p>
      )}

      {targets.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="sr-only" htmlFor="wp-push-target">
              Connected site
            </label>
            <select
              id="wp-push-target"
              className={selectCls}
              value={selected ?? ''}
              onChange={(e) => {
                setTargetId(e.target.value ? Number(e.target.value) : null);
                setCategoryChoice(null); // a new site has its own categories
                setResult(null);
              }}
            >
              <option value="" disabled>
                Select a site…
              </option>
              {targets.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          {selected != null && (
            <div className="rounded-sm border border-border-default/70 bg-canvas/40 p-2">
              <div className="mb-1 flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
                  Category
                </span>
                <span className="rounded-sm border border-border-default px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-text-tertiary">
                  Existing only
                </span>
              </div>

              {catLoading && (
                <p className="text-[11px] text-text-tertiary">Checking the site’s categories…</p>
              )}

              {!catLoading && catQuery.isError && (
                <p className="text-[11px] text-text-secondary">
                  Couldn’t read this site’s categories — the draft will be pushed without a category.
                </p>
              )}

              {!catLoading && !catQuery.isError && (
                <>
                  <p className="mb-1 text-[11px] leading-snug text-text-secondary">
                    {categoryGateNote(gate, suggestion)}
                  </p>

                  {categories.length > 0 && (
                    <>
                      <label className="sr-only" htmlFor="wp-push-category">
                        WordPress category
                      </label>
                      <select
                        id="wp-push-category"
                        className={selectCls}
                        value={gate.selectedId ?? ''}
                        onChange={(e) => {
                          setCategoryChoice(e.target.value ? Number(e.target.value) : null);
                          setResult(null);
                        }}
                      >
                        <option value="" disabled>
                          Choose a category…
                        </option>
                        {categories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                            {gate.suggestedId === c.id ? ' — suggested' : ''}
                          </option>
                        ))}
                      </select>
                      {gate.status === 'auto' && selectedCategoryName !== '' && (
                        <p className="mt-1 text-[10px] text-text-tertiary">
                          Will be filed under “{selectedCategoryName}”.
                        </p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          <div>
            <button
              type="button"
              className={primaryBtn}
              disabled={!ready || push.isPending}
              onClick={() => {
                if (ready && selected != null) {
                  setResult(null);
                  push.mutate({
                    tid: selected,
                    pushOptions: buildCategoryPushOptions(gate.selectedId, categories),
                  });
                }
              }}
            >
              {push.isPending ? 'Pushing…' : 'Push to WordPress Draft'}
            </button>
            {gate.needsChoice && !gate.canProceed && (
              <span className="ml-2 text-[10px] text-text-tertiary">
                Choose a category to enable the push.
              </span>
            )}
          </div>
        </div>
      )}

      {result && (
        <p
          role="status"
          className={
            'mt-2 text-[11px] leading-snug ' +
            (result.ok ? 'text-text-secondary' : 'text-danger')
          }
        >
          <span className="font-semibold text-text-tertiary">
            {result.ok ? 'Draft saved · ' : 'Not pushed · '}
          </span>
          {result.note}
          {result.ok && result.editUrl && (
            <>
              {' '}
              <a
                href={/^https?:\/\//i.test(result.editUrl) ? result.editUrl : undefined}
                target="_blank"
                rel="noreferrer"
                className="text-ai underline decoration-ai/40 underline-offset-2"
              >
                Open in WordPress
              </a>
            </>
          )}
        </p>
      )}
    </div>
  );
}
